import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import TonyteBackground from '../components/TonyteBackground';
import StudentEmailVerifyModal from '../components/StudentEmailVerifyModal';
import { Colors, Radius, Typography } from '../theme';
import { auth } from '../firebase';
import { useProfile, useStudiZoneChats } from '../hooks/useProfile';
import type { RootStackParamList, TonyteActivity, TonyteUser } from '../types';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { useStudiZoneEvents } from '../hooks/useStudiZoneEvents';
import {
  getStudiZoneBadges,
  isFreeStudiZoneActivity,
  matchesStudiZoneCategory,
  matchesStudiZoneSearch,
  type StudiZoneBadge,
  type StudiZoneCategory,
} from '../utils/studiZoneCategories';
import { useStudiZonePendingRequests } from '../hooks/usePlanJoinRequest';
import { isPlanActivity } from '../utils/activityKind';
import { getCurrentLocale, useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type StudiZoneTab = 'feed' | 'plans' | 'chats' | 'profile';
const CATEGORIES: Array<{ id: StudiZoneCategory; labelKey: string; icon: string }> = [
  { id: 'all', labelKey: 'studiZone.categoryAll', icon: 'grid' },
  { id: 'campus', labelKey: 'studiZone.categoryCampus', icon: 'book-open' },
  { id: 'parties', labelKey: 'studiZone.categoryParties', icon: 'music' },
  { id: 'erasmus', labelKey: 'studiZone.categoryErasmus', icon: 'globe' },
  { id: 'workshops', labelKey: 'studiZone.categoryWorkshops', icon: 'tool' },
  { id: 'sports', labelKey: 'studiZone.categorySports', icon: 'activity' },
  { id: 'free', labelKey: 'studiZone.categoryFree', icon: 'gift' },
];

type BadgeKind = StudiZoneBadge;

// Per-lane colours for card media fallbacks and badge dots. Teal/orange come
// from the Tonyte palette; the rest are tuned to sit next to them.
const LANE_STYLE: Record<BadgeKind, { gradient: string[]; dot: string; icon: string }> = {
  Campus: { gradient: ['#0B5E63', '#159B9A', '#26D0CE'], dot: '#26D0CE', icon: 'book-open' },
  Workshop: { gradient: ['#9A3F0A', '#F27D26', '#FFAE5F'], dot: '#F27D26', icon: 'tool' },
  Party: { gradient: ['#3B1B6B', '#7C3AED', '#DB2777'], dot: '#C084FC', icon: 'music' },
  Erasmus: { gradient: ['#1E3A8A', '#2563EB', '#22D3EE'], dot: '#60A5FA', icon: 'globe' },
  Sports: { gradient: ['#14532D', '#16A34A', '#A3E635'], dot: '#4ADE80', icon: 'activity' },
};

const TABS: Array<{ id: StudiZoneTab; labelKey: string; icon: string }> = [
  { id: 'feed', labelKey: 'studiZone.tabFeed', icon: 'zap' },
  { id: 'plans', labelKey: 'studiZone.tabPlans', icon: 'calendar' },
  { id: 'chats', labelKey: 'studiZone.tabChats', icon: 'message-circle' },
  { id: 'profile', labelKey: 'studiZone.tabProfile', icon: 'user' },
];

const isVerifiedStudent = (profile?: TonyteUser | null) =>
  profile?.isStudentVerified === true && profile?.studentVerificationStatus === 'verified';

const parseEventDate = (activity: TonyteActivity) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(activity.startDate || '');
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const FALLBACK_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FALLBACK_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDatePart = (date: Date, option: 'month' | 'weekday') => {
  try {
    const label = date.toLocaleDateString(getCurrentLocale(), { [option]: 'short' });
    if (label) return label.replace('.', '');
  } catch {
    // Intl unavailable — fall through to English labels.
  }
  return option === 'month' ? FALLBACK_MONTHS[date.getMonth()] : FALLBACK_WEEKDAYS[date.getDay()];
};

const getDayOffset = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / 86400000);
};

const StudiZoneScreen: React.FC = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createStudiZoneAppearanceStyles(theme), [theme]);
  const feed = useMemo(() => createFeedStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const { profile, loading: profileLoading } = useProfile(auth.currentUser?.uid || null);
  const routeInitialTab = ((route.params as { initialTab?: StudiZoneTab } | undefined)?.initialTab);
  const [activeTab, setActiveTab] = useState<StudiZoneTab>(routeInitialTab || 'feed');
  const [category, setCategory] = useState<StudiZoneCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const verified = isVerifiedStudent(profile);
  // Locked screen: "Verify Student Status" opens the university-email dialog.
  const [verifyOpen, setVerifyOpen] = useState(false);
  const university = profile?.studentUniversity || 'StudiZone';
  const firstName = (profile?.name || '').trim().split(/\s+/)[0] || '';
  const { studiZoneEvents, studiZonePlans, loading, refresh } = useStudiZoneEvents(profile);
  const currentUserId = auth.currentUser?.uid || null;
  const { chats: studiZoneChats } = useStudiZoneChats(currentUserId);
  const { requests: studiZoneRequests, loading: requestsLoading } = useStudiZonePendingRequests(currentUserId);

  useFocusEffect(
    React.useCallback(() => {
      if (routeInitialTab) setActiveTab(routeInitialTab);
      void refresh();
    }, [refresh, routeInitialTab]),
  );

  const filteredActivities = useMemo(
    () => studiZoneEvents.filter(activity =>
      matchesStudiZoneCategory(activity, category) && matchesStudiZoneSearch(activity, searchQuery),
    ),
    [studiZoneEvents, category, searchQuery],
  );
  const openActivity = (activity: TonyteActivity) => {
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source: ANALYTICS_SOURCES.FEED });
    if (isPlanActivity(activity)) {
      navigation.navigate('StudiZonePlanDetailScreen', { eventId: getResolvedEventId(activity), event: activity });
      return;
    }
    navigation.navigate('StudiZoneEventDetail', { eventId: getResolvedEventId(activity), event: activity });
  };

  const openCampusMap = () => {
    navigation.navigate('StudiZoneMap');
  };

  if (profileLoading) {
    return (
      <TonyteBackground appearanceAware style={[styles.container, appearance.container, { paddingTop: insets.top }]}>
        <View style={styles.center}>
          <ActivityIndicator color={Colors.turquoise} />
        </View>
      </TonyteBackground>
    );
  }

  if (!verified) {
    return (
      <TonyteBackground appearanceAware style={[styles.container, appearance.container, { paddingTop: insets.top + 14, paddingBottom: insets.bottom + 16 }]}>
        <View style={styles.lockedShell}>
          <TouchableOpacity style={[styles.closeButton, appearance.controlSurface]} onPress={() => navigation.goBack()}>
            <Icon name="x" size={18} color={theme.colors.icon} />
          </TouchableOpacity>
          <View style={styles.lockedCard}>
            <View style={[styles.lockedIcon, appearance.accentSurface]}>
              <Icon name="lock" size={34} color={theme.colors.accentSecondary} />
            </View>
            <Text style={[styles.lockedTitle, appearance.textPrimary]}>{t('studiZone.locked')}</Text>
            <Text style={[styles.lockedText, appearance.textSecondary]}>
              {t('studiZone.lockedSub')}
            </Text>
            <TouchableOpacity style={styles.primaryCta} onPress={() => setVerifyOpen(true)} accessibilityRole="button">
              <Text style={styles.primaryCtaText}>{t('studiZone.verifyStatus')}</Text>
            </TouchableOpacity>
          </View>
        </View>
        <StudentEmailVerifyModal
          visible={verifyOpen}
          onClose={() => setVerifyOpen(false)}
          initialEmail={profile?.studentEmail || profile?.requestedStudentEmail || profile?.email || auth.currentUser?.email || ''}
          pending={['email_verification_sent', 'email_sent', 'pending'].includes(profile?.studentVerificationStatus || '')}
        />
      </TonyteBackground>
    );
  }

  const renderActivity = ({ item }: { item: TonyteActivity }) => (
    <StudiZoneCard activity={item} onPress={() => openActivity(item)} />
  );

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
        <View style={styles.headerTitleWrap}>
          <Text style={[styles.headerGreeting, feed.textSecondary]} numberOfLines={1}>
            {firstName ? t('studiZone.hey').replace('{name}', firstName) : t('studiZone.verifiedLayer')}
          </Text>
          <Text style={[styles.title, feed.textPrimary]}>
            Studi<Text style={feed.accentText}>Zone</Text>
          </Text>
          <View style={[styles.idPill, feed.idPill]}>
            <Icon name="book" size={12} color={theme.colors.accentSecondary} />
            <Text style={[styles.idPillText, feed.textPrimary]} numberOfLines={1}>{university}</Text>
            <View style={[styles.idPillDivider, feed.divider]} />
            <Icon name="check-circle" size={12} color={theme.colors.accentSecondary} />
            <Text style={[styles.idPillVerified, feed.accentText]}>{t('studiZone.verified')}</Text>
          </View>
        </View>
        <TouchableOpacity
          style={[styles.exitButton, feed.control]}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel={t('studiZone.exit')}
        >
          <Icon name="x" size={18} color={theme.colors.icon} />
        </TouchableOpacity>
      </View>

      {activeTab === 'feed' ? (
        <>
          <View style={[styles.searchBox, feed.control]}>
            <Icon name="search" size={16} color={theme.colors.iconSubtle} />
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder={t('studiZone.searchEvents', 'Search events')}
              placeholderTextColor={theme.colors.textMuted}
              style={[styles.searchInput, feed.textPrimary]}
              returnKeyType="search"
              autoCorrect={false}
              clearButtonMode="while-editing"
              accessibilityLabel={t('studiZone.searchEvents', 'Search events')}
            />
            {searchQuery ? (
              <TouchableOpacity
                style={styles.searchClear}
                onPress={() => setSearchQuery('')}
                accessibilityRole="button"
                accessibilityLabel={t('common.clear', 'Clear')}
              >
                <Icon name="x" size={15} color={theme.colors.iconMuted} />
              </TouchableOpacity>
            ) : null}
          </View>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.categoryScroller}
            contentContainerStyle={styles.categoryRow}
          >
            {CATEGORIES.map((item, index) => {
              const active = category === item.id;
              return (
                <TouchableOpacity key={`filter-${item.id}-${index}`} style={[styles.categoryChip, active ? feed.chipActive : feed.chip]} onPress={() => setCategory(item.id)}>
                  <Icon name={item.icon} size={13} color={active ? feed.chipActiveText.color : theme.colors.iconSubtle} />
                  <Text style={[styles.categoryText, active ? feed.chipActiveText : feed.textSecondary]}>{t(item.labelKey)}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          {loading ? (
            <View style={styles.center}>
              <ActivityIndicator color={Colors.turquoise} />
              <Text style={[styles.loadingText, appearance.textMuted]}>{t('studiZone.loading')}</Text>
            </View>
          ) : (
            <FlatList
              style={styles.content}
              data={filteredActivities}
              keyExtractor={item => `event-${item.id}`}
              renderItem={renderActivity}
              contentContainerStyle={styles.list}
              showsVerticalScrollIndicator={false}
              ListHeaderComponent={
                <>
                  <CampusMapCard activities={filteredActivities} onPress={openCampusMap} />
                  {filteredActivities.length ? (
                    <View style={styles.sectionHeader}>
                      <Text style={[styles.sectionTitle, feed.textPrimary]}>{t('studiZone.upcoming')}</Text>
                      <View style={[styles.sectionCount, feed.accentSoft]}>
                        <Text style={[styles.sectionCountText, feed.accentText]}>
                          {t('studiZone.eventsCount').replace('{count}', String(filteredActivities.length))}
                        </Text>
                      </View>
                    </View>
                  ) : null}
                </>
              }
              ListEmptyComponent={<EmptyLane />}
            />
          )}
        </>
      ) : null}

      {activeTab === 'plans' ? (
        <FlatList
          style={styles.content}
          data={studiZonePlans}
          keyExtractor={item => `event-${item.id}`}
          renderItem={renderActivity}
          contentContainerStyle={styles.list}
          ListHeaderComponent={
            <TouchableOpacity style={styles.primaryCta} onPress={() => navigation.navigate('StudiZoneCreatePlanScreen')}>
              <Icon name="plus" size={17} color="#fff" />
              <Text style={styles.primaryCtaText}>{t('studiZone.createPlan')}</Text>
            </TouchableOpacity>
          }
          ListEmptyComponent={<EmptyLane />}
        />
      ) : null}

      {activeTab === 'chats' ? (
        <ScrollView style={styles.content} contentContainerStyle={styles.list}>
          {studiZoneChats.map(chat => (
            <TouchableOpacity
              key={`event-${chat.id}`}
              style={[styles.chatRow, appearance.cardSurface]}
              onPress={() => navigation.navigate('StudiZonePlanChatScreen', {
                chatId: chat.id,
                name: chat.planTitle || chat.name,
                kind: chat.kind ?? 'plan',
                photo: chat.photo,
                participantIds: chat.participantIds,
              })}
            >
              <View style={[styles.chatIcon, appearance.accentSurface]}>
                <Icon name="message-circle" size={20} color={theme.colors.accentSecondary} />
              </View>
              <View style={styles.chatCopy}>
                <Text style={[styles.chatTitle, appearance.textPrimary]} numberOfLines={1}>{chat.planTitle || chat.name}</Text>
                <Text style={[styles.chatSubtitle, appearance.textMuted]} numberOfLines={1}>{t('studiZone.planChat').replace('{count}', String(chat.participantIds.length))}</Text>
              </View>
              <Icon name="chevron-right" size={17} color={theme.colors.iconMuted} />
            </TouchableOpacity>
          ))}
          {!studiZoneChats.length ? <EmptyLane /> : null}
        </ScrollView>
      ) : null}

      {activeTab === 'profile' ? (
        <ScrollView style={styles.content} contentContainerStyle={styles.list}>
          <View style={[styles.profileCard, appearance.cardSurface]}>
            <View style={[styles.profileIcon, appearance.accentSurface]}>
              <Icon name="award" size={26} color={theme.colors.accentSecondary} />
            </View>
            <Text style={[styles.profileName, appearance.textPrimary]}>{profile?.name || t('studiZone.tonyteStudent')}</Text>
            <Text style={[styles.profileText, appearance.textSecondary]}>{t('studiZone.unlockedSub')}</Text>
          </View>
          <TouchableOpacity style={[styles.soonRow, appearance.cardSurface]} onPress={() => currentUserId && navigation.navigate('StudiZoneProfileRequestsScreen', { userId: currentUserId })}>
            <Text style={[styles.soonText, appearance.textSecondary]}>{t('studiZone.planRequests')}</Text>
            <Text style={[styles.soonBadge, appearance.textMuted]}>{requestsLoading ? '...' : studiZoneRequests.length}</Text>
          </TouchableOpacity>
          {['studiZone.studentDiscounts', 'studiZone.universityCommunities', 'studiZone.societiesClubs', 'studiZone.campusHeatmaps'].map((itemKey, index) => (
            <View key={`profile-${itemKey}-${index}`} style={[styles.soonRow, appearance.cardSurface]}>
              <Text style={[styles.soonText, appearance.textSecondary]}>{t(itemKey)}</Text>
              <Text style={[styles.soonBadge, appearance.textMuted]}>{t('studiZone.soon')}</Text>
            </View>
          ))}
        </ScrollView>
      ) : null}

      <View style={[styles.tabBar, feed.tabBar, { marginBottom: insets.bottom + 10 }]}>
        {TABS.map((tab, index) => {
          const active = activeTab === tab.id;
          return (
            <TouchableOpacity key={`tab-${tab.id}-${index}`} style={[styles.tabItem, active && feed.accentSoft]} onPress={() => setActiveTab(tab.id)}>
              <Icon name={tab.icon} size={18} color={active ? theme.colors.accentSecondary : theme.colors.iconMuted} />
              <Text style={[styles.tabText, active ? feed.accentText : feed.textMuted]}>{t(tab.labelKey)}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </TonyteBackground>
  );
};

const StudiZoneCard: React.FC<{ activity: TonyteActivity; onPress: () => void }> = ({ activity, onPress }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const feed = useMemo(() => createFeedStyles(theme), [theme]);
  const badges = getStudiZoneBadges(activity);
  const lane = LANE_STYLE[badges[0]];
  const date = parseEventDate(activity);
  const dayOffset = date ? getDayOffset(date) : null;
  const relativeDay = dayOffset === 0 ? t('studiZone.today') : dayOffset === 1 ? t('studiZone.tomorrow') : null;
  const timeLabel = [relativeDay, activity.startTime].filter(Boolean).join(' · ') || t('studiZone.timeTba');
  const location = activity.locationName || activity.location;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.card, feed.card, pressed && styles.cardPressed]}
    >
      <View style={styles.cardMedia}>
        <LinearGradient colors={lane.gradient} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={StyleSheet.absoluteFill} />
        <View pointerEvents="none" style={styles.cardMediaIcon}>
          <Icon name={lane.icon} size={92} color="rgba(255,255,255,0.16)" />
        </View>
        <LinearGradient colors={['rgba(0,0,0,0.28)', 'rgba(0,0,0,0)', 'rgba(0,0,0,0.18)']} style={StyleSheet.absoluteFill} />
        <View style={styles.cardMediaTop}>
          <View style={styles.badgeRow}>
            {badges.map((badge, index) => (
              <View key={`${activity.id}-${badge}-${index}`} style={styles.badge}>
                <View style={[styles.badgeDot, { backgroundColor: LANE_STYLE[badge].dot }]} />
                <Text style={styles.badgeText}>{badge}</Text>
              </View>
            ))}
          </View>
          {isFreeStudiZoneActivity(activity) ? (
            <View style={styles.freePill}>
              <Text style={styles.freePillText}>{t('studiZone.free')}</Text>
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.cardBody}>
        {date ? (
          <View style={[styles.dateBlock, feed.dateBlock]}>
            <Text style={[styles.dateMonth, feed.accentText]}>{formatDatePart(date, 'month')}</Text>
            <Text style={[styles.dateDay, feed.textPrimary]}>{date.getDate()}</Text>
            <Text style={[styles.dateWeekday, feed.textMuted]}>{formatDatePart(date, 'weekday')}</Text>
          </View>
        ) : null}
        <View style={styles.cardInfo}>
          <Text style={[styles.cardTitle, feed.textPrimary]} numberOfLines={2}>{activity.title}</Text>
          <View style={styles.cardMetaRow}>
            <Icon name="clock" size={12} color={theme.colors.accent} />
            <Text style={[styles.cardMetaStrong, feed.textPrimary]} numberOfLines={1}>{timeLabel}</Text>
          </View>
          {location ? (
            <View style={styles.cardMetaRow}>
              <Icon name="map-pin" size={12} color={theme.colors.iconMuted} />
              <Text style={[styles.cardMetaText, feed.textSecondary]} numberOfLines={1}>{location}</Text>
            </View>
          ) : null}
        </View>
        <View style={[styles.cardArrow, feed.accentSoft]}>
          <Icon name="arrow-up-right" size={16} color={theme.colors.accentSecondary} />
        </View>
      </View>
    </Pressable>
  );
};

const CampusMapCard: React.FC<{ activities: TonyteActivity[]; onPress: () => void }> = ({ activities, onPress }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const feed = useMemo(() => createFeedStyles(theme), [theme]);
  const mappedCount = activities.filter(activity => activity.coordinates).length;
  const previewPins = activities.slice(0, 4);

  return (
    <Pressable onPress={onPress} style={({ pressed }) => [styles.campusMapCard, feed.card, pressed && styles.cardPressed]}>
      <View style={styles.campusMapCopy}>
        <Text style={[styles.campusMapKicker, feed.accentText]}>{t('studiZone.campusMap')}</Text>
        <Text style={[styles.campusMapTitle, feed.textPrimary]} numberOfLines={2}>{t('studiZone.campusMapSub')}</Text>
        <View style={styles.campusMapFooter}>
          <View style={[styles.campusMapMetric, feed.accentSoft]}>
            <View style={[styles.liveDot, { backgroundColor: theme.colors.accentSecondary }]} />
            <Text style={[styles.campusMapMetricText, feed.accentText]}>
              {t('studiZone.studentPins').replace('{count}', String(mappedCount || activities.length))}
            </Text>
          </View>
          <Icon name="arrow-right" size={16} color={theme.colors.iconSubtle} />
        </View>
      </View>
      <View style={[styles.miniMap, feed.miniMap]}>
        <View pointerEvents="none" style={StyleSheet.absoluteFill}>
          <View style={[styles.miniStreet, styles.miniStreetOne, feed.miniStreet]} />
          <View style={[styles.miniStreet, styles.miniStreetTwo, feed.miniStreet]} />
          <View style={[styles.miniStreet, styles.miniStreetThree, feed.miniStreet]} />
          <View style={[styles.miniPark, feed.miniPark]} />
          <View style={[styles.miniCampus, feed.miniCampus]}>
            <View style={[styles.miniCampusCore, { backgroundColor: theme.colors.accentSecondary }]}>
              <Icon name="book-open" size={12} color="#fff" />
            </View>
          </View>
          {previewPins.map((activity, index) => (
            <View
              key={`pin-${activity.id}-${index}`}
              style={[styles.miniPin, MINI_PIN_POSITIONS[index], { backgroundColor: LANE_STYLE[getStudiZoneBadges(activity)[0]].dot }]}
            />
          ))}
        </View>
      </View>
    </Pressable>
  );
};

const MINI_PIN_POSITIONS = [
  { left: 18, top: 22 },
  { right: 16, top: 30 },
  { left: 26, bottom: 20 },
  { right: 24, bottom: 16 },
];

const EmptyLane = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createStudiZoneAppearanceStyles(theme), [theme]);
  return (
    <View style={[styles.empty, appearance.cardSurface]}>
      <Icon name="award" size={32} color={theme.colors.accentSecondary} />
      <Text style={[styles.emptyTitle, appearance.textPrimary]}>{t('studiZone.emptyLane')}</Text>
      <Text style={[styles.emptyText, appearance.textSecondary]}>{t('studiZone.emptyLaneSub')}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#040815' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  loadingText: { color: 'rgba(255,255,255,0.52)', fontSize: Typography.sm },
  header: {
    paddingHorizontal: 16,
    paddingBottom: 14,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerTitleWrap: { flex: 1, minWidth: 0 },
  headerGreeting: { fontSize: Typography.sm, fontWeight: Typography.semibold },
  title: { marginTop: 2, fontSize: 34, lineHeight: 40, fontWeight: Typography.extrabold, letterSpacing: -0.8 },
  idPill: {
    marginTop: 8,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    borderRadius: Radius.pill,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  idPillText: { flexShrink: 1, fontSize: 12, fontWeight: Typography.bold },
  idPillDivider: { width: 1, height: 12, marginHorizontal: 2 },
  idPillVerified: { fontSize: 12, fontWeight: Typography.extrabold },
  exitButton: {
    marginTop: 4,
    width: 42,
    height: 42,
    borderRadius: 21,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchBox: {
    height: 44,
    marginHorizontal: 16,
    marginBottom: 10,
    borderRadius: 14,
    borderWidth: 1,
    paddingLeft: 13,
    paddingRight: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  searchInput: { flex: 1, minWidth: 0, height: '100%', paddingVertical: 0, fontSize: 14, fontWeight: Typography.semibold },
  searchClear: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center' },
  categoryScroller: { maxHeight: 58, flexGrow: 0 },
  categoryRow: { paddingHorizontal: 16, paddingBottom: 14, gap: 8, alignItems: 'center' },
  categoryChip: {
    borderRadius: Radius.pill,
    borderWidth: 1,
    paddingLeft: 12,
    paddingRight: 14,
    paddingVertical: 9,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  categoryText: { fontSize: 13, fontWeight: Typography.bold },
  content: { flex: 1 },
  list: { paddingHorizontal: 16, paddingTop: 2, paddingBottom: 18 },
  sectionHeader: { marginBottom: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  sectionTitle: { fontSize: Typography.lg, fontWeight: Typography.extrabold, letterSpacing: -0.2 },
  sectionCount: { borderRadius: Radius.pill, paddingHorizontal: 10, paddingVertical: 4 },
  sectionCountText: { fontSize: 11, fontWeight: Typography.extrabold },
  card: { borderRadius: 24, borderWidth: 1, overflow: 'hidden', marginBottom: 14 },
  cardPressed: { opacity: 0.92, transform: [{ scale: 0.985 }] },
  cardMedia: { height: 124, padding: 12, overflow: 'hidden' },
  cardMediaIcon: { position: 'absolute', right: -10, bottom: -18, transform: [{ rotate: '-12deg' }] },
  cardMediaTop: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, flex: 1 },
  badge: {
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(10,12,20,0.55)',
    paddingHorizontal: 9,
    paddingVertical: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  badgeDot: { width: 6, height: 6, borderRadius: 3 },
  badgeText: { color: '#fff', fontSize: 11, fontWeight: Typography.bold },
  freePill: { borderRadius: Radius.pill, backgroundColor: '#fff', paddingHorizontal: 10, paddingVertical: 5 },
  freePillText: { color: '#0B5E63', fontSize: 11, fontWeight: Typography.extrabold },
  cardBody: { padding: 14, flexDirection: 'row', alignItems: 'center', gap: 12 },
  dateBlock: { width: 54, borderRadius: 16, borderWidth: 1, paddingVertical: 7, alignItems: 'center' },
  dateMonth: { fontSize: 10, fontWeight: Typography.extrabold, textTransform: 'uppercase', letterSpacing: 1 },
  dateDay: { fontSize: 22, lineHeight: 26, fontWeight: Typography.extrabold },
  dateWeekday: { fontSize: 10, fontWeight: Typography.bold },
  cardInfo: { flex: 1, minWidth: 0, gap: 5 },
  cardTitle: { fontSize: Typography.md, lineHeight: 22, fontWeight: Typography.extrabold },
  cardMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  cardMetaStrong: { flexShrink: 1, fontSize: 12, fontWeight: Typography.bold },
  cardMetaText: { flexShrink: 1, fontSize: 12, fontWeight: Typography.semibold },
  cardArrow: { alignSelf: 'flex-start', width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center' },
  empty: { borderRadius: 30, borderWidth: 1, borderColor: 'rgba(186,230,253,0.14)', backgroundColor: 'rgba(255,255,255,0.04)', padding: 28, alignItems: 'center' },
  emptyTitle: { marginTop: 12, color: '#fff', fontSize: Typography.lg, fontWeight: Typography.extrabold, textAlign: 'center' },
  emptyText: { marginTop: 8, color: 'rgba(255,255,255,0.52)', fontSize: Typography.sm, lineHeight: 20, textAlign: 'center' },
  campusMapCard: {
    borderRadius: 24,
    borderWidth: 1,
    padding: 14,
    paddingLeft: 18,
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 14,
    marginBottom: 22,
  },
  campusMapCopy: { flex: 1, minWidth: 0, justifyContent: 'space-between', paddingVertical: 4 },
  campusMapKicker: { fontSize: 10, fontWeight: Typography.extrabold, letterSpacing: 1.6, textTransform: 'uppercase' },
  campusMapTitle: { marginTop: 6, fontSize: Typography.base, lineHeight: 20, fontWeight: Typography.extrabold },
  campusMapFooter: { marginTop: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  campusMapMetric: { borderRadius: Radius.pill, paddingHorizontal: 10, paddingVertical: 6, flexDirection: 'row', alignItems: 'center', gap: 6 },
  liveDot: { width: 6, height: 6, borderRadius: 3 },
  campusMapMetricText: { fontSize: 11, fontWeight: Typography.extrabold },
  miniMap: { width: 112, height: 112, borderRadius: 18, borderWidth: 1, overflow: 'hidden' },
  miniStreet: { position: 'absolute', height: 3, borderRadius: 2 },
  miniStreetOne: { left: -20, right: -20, top: 44, transform: [{ rotate: '-14deg' }] },
  miniStreetTwo: { left: -20, right: -20, top: 78, transform: [{ rotate: '9deg' }] },
  miniStreetThree: { width: 3, height: 150, left: 62, top: -20, transform: [{ rotate: '18deg' }] },
  miniPark: { position: 'absolute', width: 34, height: 26, borderRadius: 9, left: 8, top: 52 },
  miniCampus: { position: 'absolute', width: 48, height: 48, borderRadius: 24, left: 32, top: 32, alignItems: 'center', justifyContent: 'center' },
  miniCampusCore: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: '#fff' },
  miniPin: { position: 'absolute', width: 12, height: 12, borderRadius: 6, borderWidth: 2, borderColor: '#fff' },
  chatRow: { minHeight: 70, borderRadius: 24, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.045)', padding: 12, flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 },
  chatIcon: { width: 48, height: 48, borderRadius: 18, backgroundColor: 'rgba(125,211,252,0.1)', alignItems: 'center', justifyContent: 'center' },
  chatCopy: { flex: 1, minWidth: 0 },
  chatTitle: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.extrabold },
  chatSubtitle: { marginTop: 4, color: 'rgba(255,255,255,0.48)', fontSize: Typography.xs },
  profileCard: { borderRadius: 32, borderWidth: 1, borderColor: 'rgba(186,230,253,0.14)', backgroundColor: 'rgba(255,255,255,0.045)', padding: 22, alignItems: 'center', marginBottom: 12 },
  profileIcon: { width: 58, height: 58, borderRadius: 22, backgroundColor: 'rgba(125,211,252,0.1)', alignItems: 'center', justifyContent: 'center' },
  profileName: { marginTop: 14, color: '#fff', fontSize: Typography.xl, fontWeight: Typography.extrabold },
  profileText: { marginTop: 8, color: 'rgba(255,255,255,0.55)', fontSize: Typography.sm, lineHeight: 20, textAlign: 'center' },
  soonRow: { minHeight: 52, borderRadius: 20, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.04)', paddingHorizontal: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  soonText: { color: 'rgba(255,255,255,0.78)', fontSize: Typography.sm, fontWeight: Typography.bold },
  soonBadge: { color: 'rgba(255,255,255,0.34)', fontSize: 10, fontWeight: Typography.extrabold, textTransform: 'uppercase', letterSpacing: 1.2 },
  tabBar: { marginHorizontal: 12, borderRadius: 28, borderWidth: 1, padding: 6, flexDirection: 'row', justifyContent: 'space-between' },
  tabItem: { flex: 1, borderRadius: 22, alignItems: 'center', paddingVertical: 9, gap: 4 },
  tabText: { fontSize: 10, fontWeight: Typography.extrabold, letterSpacing: 0.2 },
  lockedShell: { flex: 1, paddingHorizontal: 18 },
  closeButton: { alignSelf: 'flex-end', width: 42, height: 42, borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.06)', alignItems: 'center', justifyContent: 'center' },
  lockedCard: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 },
  lockedIcon: { width: 88, height: 88, borderRadius: 30, borderWidth: 1, borderColor: 'rgba(186,230,253,0.18)', backgroundColor: 'rgba(125,211,252,0.09)', alignItems: 'center', justifyContent: 'center' },
  lockedTitle: { marginTop: 24, color: '#fff', fontSize: 30, lineHeight: 36, fontWeight: Typography.extrabold, textAlign: 'center' },
  lockedText: { marginTop: 12, color: 'rgba(255,255,255,0.65)', fontSize: Typography.sm, lineHeight: 21, textAlign: 'center' },
  primaryCta: { minHeight: 52, borderRadius: 24, backgroundColor: '#F27D26', paddingHorizontal: 18, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8, marginBottom: 12 },
  primaryCtaText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.extrabold },
});

const createStudiZoneAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  accentText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  controlSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  accentSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.borderStrong,
  }),
  cardSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
});

// Fully theme-driven styles for the redesigned feed. Surfaces are opaque so
// Android elevation doesn't bleed through translucent cards.
const createFeedStyles = (theme: TonyteTheme) => {
  const dark = theme.isDark;
  const surface = dark ? '#111826' : theme.colors.surfaceElevated;
  return StyleSheet.create({
    textPrimary: { color: theme.colors.textPrimary },
    textSecondary: { color: theme.colors.textSecondary },
    textMuted: { color: theme.colors.textMuted },
    accentText: { color: theme.colors.accentSecondary },
    accentSoft: { backgroundColor: dark ? 'rgba(38,208,206,0.12)' : 'rgba(21,155,154,0.10)' },
    divider: { backgroundColor: theme.colors.borderStrong },
    control: { backgroundColor: surface, borderColor: theme.colors.border },
    idPill: {
      backgroundColor: dark ? 'rgba(38,208,206,0.08)' : 'rgba(255,252,246,0.9)',
      borderColor: theme.colors.accentSecondaryBorder,
    },
    chip: { backgroundColor: surface, borderColor: theme.colors.border },
    chipActive: { backgroundColor: theme.colors.accentSecondary, borderColor: theme.colors.accentSecondary },
    chipActiveText: { color: dark ? '#04201F' : '#FFFFFF' },
    card: { backgroundColor: surface, borderColor: theme.colors.cardBorder, ...theme.shadows.card },
    dateBlock: {
      backgroundColor: dark ? 'rgba(255,255,255,0.04)' : theme.colors.surface,
      borderColor: theme.colors.border,
    },
    miniMap: {
      backgroundColor: dark ? '#0B1C24' : '#E7F1EC',
      borderColor: theme.colors.border,
    },
    miniStreet: { backgroundColor: dark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.95)' },
    miniPark: { backgroundColor: dark ? 'rgba(74,222,128,0.12)' : 'rgba(22,163,74,0.14)' },
    miniCampus: { backgroundColor: dark ? 'rgba(38,208,206,0.18)' : 'rgba(21,155,154,0.16)' },
    tabBar: {
      backgroundColor: dark ? '#0D131F' : theme.colors.surfaceElevated,
      borderColor: theme.colors.border,
      ...theme.shadows.elevated,
    },
  });
};

export default StudiZoneScreen;
